import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInsuranceStore } from 'src/store/insurance';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useFinalSubmissionStore } from 'src/store/final-submission';
import { usePaymentStore } from '@features/onboarding/components/7-finish/store/payment';
import { usePlan } from '@features/onboarding/hooks/usePlan';

import { NoWorriesStep } from './components/no-worries/NoWorriesStep';
import { ContractStep } from './components/contract-step';
import { CoverageReassurance } from './components/coverage-reassurance/CoverageReassurance';
import { CoverageReassuranceWithExcuse } from './components/coverage-reassurance-with-excuse/CoverageReassuranceWithExcuse';
import { ExcuseStep } from './components/excuse-step/ExcuseStep';
import { UnderwritingResult } from './UnderwritingResult';
import { finishStepsContent } from './content/content';
import { PaymentStep } from './components/stripe-payment/PaymentStep';
import { CongratulationsStep } from '@features/onboarding/components/7-finish/components/congratulations/CongratulationsStep';
import { HumanReviewStep } from '@features/onboarding/components/7-finish/components/human-review/HumanReviewStep';
import { useUnderReviewStore } from 'src/store/under-review';

import { cn } from '@utils/index';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { useApplicationStore } from 'src/store/application';

const components = {
  NoWorriesStep,
  PaymentStep,
  CoverageReassurance,
  CoverageReassuranceWithExcuse,
  UnderwritingResult,
  ExcuseStep,
  ContractStep,
  CongratulationsStep,
  HumanReviewStep,
} as any;

const DynamicComponent = ({
  componentName,
  handleNextCompatible,
  setContainerClassName,
  handlePrev,
}: any) => {
  const Component = components[componentName] || 'NoWorriesStep';

  // Для PaymentStep передаем onSubmit как handleNextCompatible
  if (componentName === 'PaymentStep') {
    return (
      <Component
        onSubmit={handleNextCompatible}
        setContainerClassName={setContainerClassName}
      />
    );
  }

  // Для CongratulationsStep передаем onSubmit как handleNextCompatible
  if (componentName === 'CongratulationsStep') {
    return (
      <Component
        question={{ question: 'Let’s See Your Final Quote' }}
        onSubmit={handleNextCompatible}
      />
    );
  }

  return (
    <Component
      handleNextCompatible={handleNextCompatible}
      setContainerClassName={setContainerClassName}
      handlePrev={handlePrev}
    />
  );
};

export const Finish = () => {
  const navigate = useNavigate();
  const { data } = useOnboardingStore();
  const { isLocalLoading, handlePreviousStep, handleJumpTo } = useFormControls();
  const { plans: chosenPlans } = usePlan();
  const { isUnderReview } = useUnderReviewStore();
  const plans = useInsuranceStore((state) => state.plans);
  const [showPayment, setShowPayment] = useState(false);
  const [showConfirmSign, setShowConfirmSign] = useState(false);
  const [isOkReject, setIsOkReject] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const { signedAt } = useApplicationStore();

  const medicalPlans = React.useMemo(
    () => chosenPlans.filter((plan) => plan.baseKey === 'majormedical'),
    [chosenPlans]
  );
  const otherPlans = React.useMemo(
    () => chosenPlans.filter((plan) => plan.baseKey !== 'majormedical'),
    [chosenPlans]
  );
  const firstName = React.useMemo(() => {
    const primary = data?.applicants?.find((a) => a.applicant === 'primary');
    return primary?.firstName || '';
  }, [data]);
  const hasSelectedMedicalPlan = medicalPlans.length > 0;
  const hasOtherSelectedPlans = otherPlans.length > 0;
  const {
    isUnderwritingLoading,
    underwritingResult,
    isSubmittingApplication,
    submitFinalApplication,
  } = useFinalSubmissionStore();

  const { paymentMethodId } = usePaymentStore();

  const hasAnyPlan = !!chosenPlans.length;

  const medicalPriceInfo = React.useMemo(() => {
    if (!hasSelectedMedicalPlan) return null;
    const selectedMedical = medicalPlans[0];
    // выбранный план в списке всех планов по uid
    const fullPlan = plans.find((p) => p.uid === (selectedMedical as any)?.id);
    if (!fullPlan) return null;
    const originalPrice = fullPlan.averagePrice;
    const currentPrice = (selectedMedical as any)?.price ?? 0;
    return {
      originalPrice,
      currentPrice,
      isHigherPrice: currentPrice > originalPrice,
    };
  }, [hasSelectedMedicalPlan, medicalPlans, plans]);

  const finishStep = hasAnyPlan
    ? finishStepsContent.find((s) => s.id === 'congratulations')
    : finishStepsContent.find((s) => s.id === 'coverageReassurance');

  useEffect(() => {
    if (!paymentMethodId) return;
    if (
      underwritingResult?.status === 'rejected' &&
      !isOkReject &&
      !hasOtherSelectedPlans
    )
      return;

    const updatedFormData = useOnboardingStore.getState().data;
    const applicationData = transformApplicationData(updatedFormData, plans);

    const filteredApplicationData = (() => {
      if (underwritingResult?.status !== 'rejected') return applicationData;

      const insurancePlanById = new Map<number, (typeof plans)[number]>(
        plans
          .filter((p) => typeof p.id === 'number')
          .map((p) => [p.id as number, p])
      );

      const nonMedicalPlans = applicationData.plans.filter((p) => {
        const src = insurancePlanById.get(p.id);
        return src ? src.category !== 'Major Medical' : true;
      });

      return { ...applicationData, plans: nonMedicalPlans };
    })();

    const startFinalRequest = async (token: string) => {
      await submitFinalApplication(filteredApplicationData, token);
      navigate('/thanks');
    };

    startFinalRequest(paymentMethodId);
  }, [
    paymentMethodId,
    isOkReject,
    underwritingResult?.status,
    plans,
    submitFinalApplication,
    navigate,
  ]);

  // Показываем поздравления только если статус — accepted
  useEffect(() => {
    setShowCongratulations(underwritingResult?.status === 'accepted');
  }, [underwritingResult?.status]);

  type FinishUI = {
    title?: string;
    description?: string;
    nextButtonLabel?: string;
    hideNextButton?: boolean;
    key?: string;
    componentName:
      | 'NoWorriesStep'
      | 'PaymentStep'
      | 'CoverageReassurance'
      | 'CoverageReassuranceWithExcuse'
      | 'UnderwritingResult'
      | 'ExcuseStep'
      | 'ContractStep'
      | 'CongratulationsStep'
      | 'HumanReviewStep';
  };

  const ui: FinishUI = React.useMemo(() => {
    if (underwritingResult?.status === 'human_review' || isUnderReview) {
      return {
        title: `Application Review Required`,
        description: '',
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'HumanReviewStep',
        key: 'humanReview',
      };
    }

    if (showCongratulations) {
      return {
        title: 'Congratulations, your health plan was approved!',
        description: 'You\'ve successfully completed your health questionnaire. Let\'s finish up your application!',
        nextButtonLabel: 'Next',
        hideNextButton: false,
        componentName: 'CongratulationsStep',
        key: 'congratulations',
      };
    }

    if (!hasAnyPlan) {
      return {
        title: finishStep?.title,
        description: finishStep?.description,
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'NoWorriesStep',
        key: 'noWorries',
      };
    }

    if (showPayment) {
      return {        
        title: "Securely connect & pay",
        description:
          "In this final step, you’ll set up your ACH payment by linking your bank account through our secure system. It’s fast, safe, and once connected, future payments are automatic — no extra steps needed.",
        nextButtonLabel: 'Submit',
        hideNextButton: true,
        componentName: 'PaymentStep',
        key: 'payment',
      };
    }
    if (
      underwritingResult?.status === 'accepted' ||
      underwritingResult?.status === undefined
    ) {
      // Skip contract step if already signed
      if (showConfirmSign && !signedAt) {
        return {
          title: "Sign here, pay next, and you're done!",
          description:
            "You're almost at the finish line! Review and e-sign our standard insurance contract—it only takes a minute. If now's not a good time, no worries—we've also emailed you a link so you can sign later. Once it's signed, you'll just set up your payment details and you're all set.",
          nextButtonLabel: 'Payment',
          hideNextButton: true,
          componentName: 'ContractStep',
          key: 'contract',
        };
      }
      if (!hasSelectedMedicalPlan && hasOtherSelectedPlans) {
        return {
          title: `${firstName ? firstName + ', ' : ''}your plan summary is ready!`,
          description: "You’re all set with your coverage — here’s your final monthly price.",
          nextButtonLabel: 'Payment',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
      if (hasSelectedMedicalPlan && hasOtherSelectedPlans && !signedAt) {
        const otherPlanName = otherPlans.length === 1 ? otherPlans[0]?.name : null;
        const sameCopy = otherPlanName
          ?
            'You’ve made some smart choices\n\n' +
            'Here’s the great news:\n' +
            `<ul class="ul-with-point mt-2"><li class="!font-secondary">The quote for the ${otherPlanName} remains the same.</li>` +
            '<li class="!font-secondary">Even better — your healthcare plan came in right on target. The final quote matches what we first showed you. No surprises, just the coverage you expected.</li></ul>\n' +
            'Curious about alternatives? Every health plan now comes with your own personalized quote, so you can explore with confidence.'
          :
            'You’ve made some smart choices\n\n' +
            'Here’s the great news:\n' +
            '<ul class="ul-with-point mt-2"><li class="!font-secondary">The quote for every plan you selected, except the health plan, remains the same.</li>' +
            '<li class="!font-secondary">Even better — your healthcare plan came in right on target. The final quote matches what we first showed you. No surprises, just the coverage you expected.</li></ul>\n' +
            'Curious about alternatives? Every health plan now comes with your own personalized quote, so you can explore with confidence.';
        const higherCopy =
          'You\'ve made some great choices\n\n' +
          'The good news: the quote for any plan, other than your health plan, remains the same.\n\n' +
          'Regarding your health plan quote – After reviewing your application, your final monthly price is a little higher than the estimate. We calculate this based on the answers you supplied in your personal health questionnaire.\n\n' +
          'Want to explore other options? No problem—every health plan now displays a personalized quote.';

        return {
          title: `${firstName ? firstName + ', ' : ''}your plan summary is ready!`,
          description:
            medicalPriceInfo?.isHigherPrice
              ? higherCopy
              : sameCopy,
          nextButtonLabel: 'Confirm & Sign',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
      if (hasSelectedMedicalPlan && !hasOtherSelectedPlans && !signedAt) {
        const sameCopy =
          'You’re all set with your coverage — here’s your final monthly price.\n\n' +
          'Great news — the final quote for the healthcare plan you chose is exactly the same as what we first showed you! No surprises here, just the coverage you expected.\n\n' +
          'Want to explore other options? No problem—every health plan now displays a personalized quote.';
        const higherCopy =
          'You’re all set with your coverage — here’s your final monthly price.\n\n' +
          'Regarding your health plan quote – After reviewing your application, your final monthly price is a little higher than the estimate. We calculate this based on the answers you supplied in your personal health questionnaire.\n\n' +
          'Want to explore other options? No problem—every health plan now displays a personalized quote.';

        return {
          title: `${firstName ? firstName + ', ' : ''}your plan summary is ready!`,
          description: 
            medicalPriceInfo?.isHigherPrice
              ? higherCopy
              : sameCopy,
          nextButtonLabel: 'Confirm & Sign',
          hideNextButton: false,
          componentName: 'CoverageReassurance',
          key: 'coverageReassurance',
        };
      }
    }

    if (underwritingResult?.status === 'rejected') {
      if (hasOtherSelectedPlans) {
        return {
          title:
            'We Can’t Approve Health Coverage Right Now — But You’re Approved for your other coverage!',
          description:
            'While we’re not able to offer you a health plan right now, you’re fully approved for the other coverage you selected. The great news is that you can still move forward today with these plans and enjoy the protection they provide. And remember—you’re welcome to reapply for health coverage in the future if your situation changes.',
          nextButtonLabel: 'Payment',
          hideNextButton: false,
          componentName: 'CoverageReassuranceWithExcuse',
          key: 'coverageReassuranceWithExcuse',
        };
      }

      // Only show rejection screen if no other plans are selected
      return {
        title: 'You Still Have a Path to Coverage!',
        description:
          "We can't offer you one of our health plans this time, but that doesn't mean the search is over.\n\n" +
          'You may qualify for an ACA plan, and you can see your instant quotes in just one click. Coverage could be closer than you think!',
        nextButtonLabel: undefined,
        hideNextButton: true,
        componentName: 'ExcuseStep',
        key: 'excuse',
      };
    }

    return {
      title: "We're finalizing your quote",
      description:
        "You've been approved for your medical insurance, and your final premium has been displayed and locked in!\n\n" +
        "But remember, you're not covered yet! When you click Finish Up, you'll be taken to a page where you can enter your payment info. Once that's done, we'll send you an email with your plan start dates and a link to your personal Health Portal. From there, you can download insurance cards, find doctors, manage billing, and get in touch with customer service anytime.\n\n" +
        'Great job taking this important step toward protecting your health!',
      nextButtonLabel:
        hasSelectedMedicalPlan && hasSelectedMedicalPlan && !signedAt
          ? 'Confirm & Sign'
          : 'Payment',
      hideNextButton: false,
      componentName: 'CoverageReassurance',
      key: 'coverageReassurance',
    };
  }, [
    showCongratulations,
    hasAnyPlan,
    finishStep,
    underwritingResult,
    hasSelectedMedicalPlan,
    hasOtherSelectedPlans,
    firstName,
    hasSelectedMedicalPlan,
    showConfirmSign,
    showPayment,
    isOkReject,
    signedAt,
  ]);

  const { title, description, nextButtonLabel, hideNextButton, componentName, key } =
    ui;

  const handlePrev = (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    if (underwritingResult?.status === 'human_review') {
      return;
    }
    if (showCongratulations) {
      // Не позволяем вернуться с экрана поздравлений
      return;
    }
    if (showPayment) {
      setShowPayment(false);
      return;
    }
    if (showConfirmSign) {
      setShowConfirmSign(false);
      return;
    }
    if (underwritingResult?.status === 'rejected') {
      useOnboardingStore.getState().removeMedicalPlan();
    }
    if (underwritingResult?.status === 'rejected' && !hasOtherSelectedPlans) {
      handleJumpTo(FormStepSections.Dental);
      return;
    }
    if (
      underwritingResult?.status === 'rejected' &&
      !isOkReject &&
      hasOtherSelectedPlans
    ) {
      setIsOkReject(true);
      return;
    }
    handlePreviousStep();
  };

  const handleNextCompatible = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();

    if (underwritingResult?.status === 'human_review') {
      return;
    }

    if (nextButtonLabel === 'Next' && showCongratulations) {
      setShowCongratulations(false);
      return;
    }
    if (nextButtonLabel === 'Payment') {
      setShowPayment(true);
      setShowConfirmSign(false);
      return;
    }
    if (nextButtonLabel === 'Confirm & Sign') {
      setShowConfirmSign(true);
      return;
    }
    if (underwritingResult?.status === 'rejected' && isOkReject) {
      setShowPayment(true);
      return;
    }
    if (nextButtonLabel === 'Submit') {
      if (!hasSubmitted) {
        // const stripeForm = document.getElementById(
        //   'stripeForm'
        // ) as HTMLFormElement;
        // if (stripeForm) {
        //   const submitEvent = new Event('submit', {
        //     bubbles: true,
        //     cancelable: true,
        //   });
        //   stripeForm.dispatchEvent(submitEvent);
        //   setHasSubmitted(true);
        //   setTimeout(() => setHasSubmitted(false), 5000);
        // }
      }
      return;
    }
  };

  const shouldShowSubmitText = () => {
    if (isUnderwritingLoading || isSubmittingApplication) return false;

    if (!hasAnyPlan) {
      return showPayment;
    }

    return false;
  };

  useEffect(() => {
    setIsOkReject(false);
  }, []);

  return (
    <OnboardingFormStepLayout
      handleFormSubmit={handleNextCompatible}
      onPrev={showCongratulations || underwritingResult?.status === 'human_review' ? undefined : handlePrev}
      title={title}
      description={description}
      hideNextButton={hideNextButton}
      isSubmitText={!!shouldShowSubmitText()}
      onlyImage={!hasAnyPlan}
      // isLocalLoading - TODO не меняется PV-261
      isLoading={isUnderwritingLoading || isSubmittingApplication || isLocalLoading}
      nextButtonLabel={nextButtonLabel}
      headerAnimationKey={key}
    >
      <div className={cn('h-full w-full rounded-2xl')}>
        <div className="pb-4 h-full w-full justify-center">
          <DynamicComponent
            handlePrev={handlePrev}
            handleNextCompatible={handleNextCompatible}
            componentName={componentName}
          />
        </div>
      </div>
    </OnboardingFormStepLayout>
  );
};
